import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
	Alert,
	Box,
	Button,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const API = process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN;

interface CustomFactor {
	factorID: number;
	name: string;
	factorValue: number;
	unit: string;
	validFromYear: number;
	validToYear: number | null;
	notes: string | null;
}

interface MarketYear {
	year: number;
	marketBasedTonnes: number;
	locationBasedTonnes: number | null;
	entries: number;
}

/**
 * Custom emission factors (electricity contracts) + market-based Scope 2
 * comparison. Segmentation P2 items 14 and 12.
 */
const CustomFactors: React.FC = () => {
	const { getAccessTokenSilently } = useAuth0();
	const [factors, setFactors] = useState<CustomFactor[]>([]);
	const [years, setYears] = useState<MarketYear[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	const currentYear = new Date().getFullYear();
	const [form, setForm] = useState({
		sourceType: "electricity",
		name: "",
		factorValue: "",
		validFromYear: String(currentYear),
		validToYear: "",
		notes: "",
	});

	const authFetch = useCallback(
		async (path: string, init?: RequestInit) => {
			const token = await getAccessTokenSilently();
			return fetch(`${API}${path}`, {
				...init,
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
					...(init?.headers || {}),
				},
			});
		},
		[getAccessTokenSilently],
	);

	const load = useCallback(async () => {
		setError(null);
		try {
			const [fRes, mRes] = await Promise.all([
				authFetch("/api/custom-factors"),
				authFetch("/api/scope2/market-based"),
			]);
			if (fRes.ok) {
				const data = await fRes.json();
				setFactors((data.Data || data).factors || []);
			}
			if (mRes.ok) {
				const data = await mRes.json();
				setYears((data.Data || data).years || []);
			}
		} catch {
			setError("Could not load custom factors. Try refreshing the page.");
		}
	}, [authFetch]);

	useEffect(() => {
		load();
	}, [load]);

	const handleSave = async () => {
		setSaving(true);
		setError(null);
		try {
			const res = await authFetch("/api/custom-factors", {
				method: "POST",
				body: JSON.stringify({
					sourceType: form.sourceType,
					name: form.name,
					factorValue: form.factorValue,
					validFromYear: form.validFromYear,
					validToYear: form.validToYear || null,
					notes: form.notes || undefined,
				}),
			});
			if (!res.ok) {
				const data = await res.json().catch(() => ({}));
				setError(data.Message || "Could not save the factor.");
				return;
			}
			setForm({
				sourceType: form.sourceType,
				name: "",
				factorValue: "",
				validFromYear: String(currentYear),
				validToYear: "",
				notes: "",
			});
			await load();
		} finally {
			setSaving(false);
		}
	};

	const handleRemove = async (factorID: number) => {
		await authFetch(`/api/custom-factors?factorId=${factorID}`, {
			method: "DELETE",
		});
		await load();
	};

	return (
		<Box maxWidth={900} margin="0 auto" padding={3}>
			<Typography variant="h5" gutterBottom>
				Custom emission factors
			</Typography>
			<Typography variant="body2" color="text.secondary" gutterBottom>
				Add electricity factors from your own contracts — GreenPower,
				power purchase agreements, or your retailer&apos;s residual mix
				— in kg CO2e per kWh. Your market-based Scope 2 emissions are
				calculated with these factors alongside the standard
				location-based method.
			</Typography>

			{error && (
				<Alert severity="warning" sx={{ my: 2 }}>
					{error}
				</Alert>
			)}

			<Paper
				elevation={0}
				sx={{ p: 2, my: 2, border: "1px solid rgba(0,23,28,0.10)" }}
			>
				<Typography variant="subtitle1" gutterBottom>
					Add a factor
				</Typography>
				<Box display="flex" gap={2} flexWrap="wrap">
					<TextField
						select
						label="Type"
						size="small"
						value={form.sourceType}
						onChange={(e) =>
							setForm({ ...form, sourceType: e.target.value })
						}
						sx={{ minWidth: 190 }}
						SelectProps={{ native: true }}
					>
						<option value="electricity">
							Electricity (kgCO2e/kWh)
						</option>
						<option value="supplier-spend">
							Supplier spend (kgCO2e/AUD)
						</option>
					</TextField>
					<TextField
						label="Name (e.g. GreenPower 100%)"
						size="small"
						value={form.name}
						onChange={(e) =>
							setForm({ ...form, name: e.target.value })
						}
						sx={{ minWidth: 220 }}
					/>
					<TextField
						label={form.sourceType === "electricity" ? "kg CO2e per kWh" : "kg CO2e per AUD"}
						size="small"
						value={form.factorValue}
						onChange={(e) =>
							setForm({ ...form, factorValue: e.target.value })
						}
						sx={{ width: 150 }}
					/>
					<TextField
						label="From year"
						size="small"
						value={form.validFromYear}
						onChange={(e) =>
							setForm({ ...form, validFromYear: e.target.value })
						}
						sx={{ width: 110 }}
					/>
					<TextField
						label="To year (optional)"
						size="small"
						value={form.validToYear}
						onChange={(e) =>
							setForm({ ...form, validToYear: e.target.value })
						}
						sx={{ width: 140 }}
					/>
					<Button
						variant="contained"
						color="secondary"
						disabled={saving}
						onClick={handleSave}
					>
						{saving ? "Saving…" : "Save factor"}
					</Button>
				</Box>
			</Paper>

			<Paper
				elevation={0}
				sx={{ p: 2, my: 2, border: "1px solid rgba(0,23,28,0.10)" }}
			>
				<Typography variant="subtitle1" gutterBottom>
					Your factors
				</Typography>
				{factors.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						No custom factors yet. Add your first contract factor
						above.
					</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Name</TableCell>
								<TableCell>kg CO2e/kWh</TableCell>
								<TableCell>Valid</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{factors.map((f) => (
								<TableRow key={f.factorID}>
									<TableCell>{f.name}</TableCell>
									<TableCell>{f.factorValue}</TableCell>
									<TableCell>
										{f.validFromYear}–
										{f.validToYear ?? "ongoing"}
									</TableCell>
									<TableCell align="right">
										<IconButton
											size="small"
											aria-label="Remove factor"
											onClick={() =>
												handleRemove(f.factorID)
											}
										>
											<DeleteOutlineIcon fontSize="small" />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</Paper>

			<Paper
				elevation={0}
				sx={{ p: 2, my: 2, border: "1px solid rgba(0,23,28,0.10)" }}
			>
				<Typography variant="subtitle1" gutterBottom>
					Market-based vs location-based Scope 2
				</Typography>
				{years.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						Once you add a factor covering a year with electricity
						data, the comparison appears here.
					</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Year</TableCell>
								<TableCell>Market-based (t CO2e)</TableCell>
								<TableCell>Location-based (t CO2e)</TableCell>
								<TableCell>Entries covered</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{years.map((y) => (
								<TableRow key={y.year}>
									<TableCell>{y.year}</TableCell>
									<TableCell>{y.marketBasedTonnes}</TableCell>
									<TableCell>
										{y.locationBasedTonnes ?? "—"}
									</TableCell>
									<TableCell>{y.entries}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</Paper>
		</Box>
	);
};

export default CustomFactors;
